import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Plus,
  Trash2,
  Save,
  FileText,
  User,
  Search,
  ChevronDown,
  Check,
  BookOpen,
  Percent,
  Tag,
  Sparkles,
  Loader2,
  AlertTriangle,
  AlertCircle,
  ArrowRight,
  Edit3,
  RefreshCw,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  InvoiceItem,
  InvoicePayload,
  StationeryItem,
  createInvoice,
  updateInvoice,
  getStationeries,
  searchStudentsServerSide,
  getStudentInvoices,
} from "../../services/admin";
import { generateAcademicSessions } from "../../utilities/academicSession";
import { useDebounce } from "../../hooks/useDebounce";

interface StudentOption {
  id: string;
  name: string;
  email?: string;
  classroom?: { id?: string; name: string };
}

interface InvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preselectedStudent?: StudentOption | null;
  preselectedStationeryId?: string | null;
  initialData?: InvoiceItem | null;
  studentList?: StudentOption[];
  stationeryList?: StationeryItem[];
}

export const InvoiceModal: React.FC<InvoiceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  preselectedStudent,
  preselectedStationeryId,
  initialData,
  studentList: initialStudentList = [],
  stationeryList: initialStationeryList = [],
}) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [allStudents, setAllStudents] = useState<StudentOption[]>(initialStudentList);
  const [fetchingStudents, setFetchingStudents] = useState<boolean>(false);
  const [selectedStudentObj, setSelectedStudentObj] = useState<StudentOption | null>(preselectedStudent || null);

  const [allStationeries, setAllStationeries] = useState<StationeryItem[]>(initialStationeryList);
  const [fetchingStationeries, setFetchingStationeries] = useState<boolean>(false);

  // Existing Invoices for Student UX
  const [existingStudentInvoices, setExistingStudentInvoices] = useState<InvoiceItem[]>([]);
  const [fetchingExistingInvoices, setFetchingExistingInvoices] = useState<boolean>(false);
  const [currentEditingInvoice, setCurrentEditingInvoice] = useState<InvoiceItem | null>(initialData || null);

  // Searchable Select State
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const debouncedStudentSearch = useDebounce(searchQuery, 400);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Form states
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");
  const [academicSession, setAcademicSession] = useState<string>("2026/2027");
  const [term, setTerm] = useState<string | number>("1");
  const [dueDate, setDueDate] = useState<string>("");
  const [notes, setNotes] = useState<string>("Term fee invoice");

  // Dynamic Stationery Items with percentage discounts
  const [stationaryRows, setStationaryRows] = useState<
    {
      stationary_id: string;
      quantity: number | string;
      discount_percentage?: number | string;
      customDescription?: string;
      customUnitPrice?: number;
    }[]
  >([{ stationary_id: "", quantity: 1, discount_percentage: 0 }]);

  // Track which rows have discount mode enabled/open
  const [activeDiscountRows, setActiveDiscountRows] = useState<number[]>([]);

  // Load Stationeries catalog
  const loadStationeries = async () => {
    setFetchingStationeries(true);
    try {
      const res = await getStationeries();
      const rawArray = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];
      setAllStationeries(rawArray);
    } catch (e) {
      console.error("Error loading stationeries in InvoiceModal:", e);
    } finally {
      setFetchingStationeries(false);
    }
  };

  // Server-side Student Search (first 10 items or matching query)
  useEffect(() => {
    if (!isOpen) return;

    if (preselectedStudent) {
      setAllStudents([preselectedStudent]);
      setSelectedStudentObj(preselectedStudent);
      return;
    }

    const fetchStudents = async () => {
      setFetchingStudents(true);
      try {
        const students = await searchStudentsServerSide(debouncedStudentSearch, 1, 10);
        setAllStudents(students);
      } catch (e) {
        console.error("Error loading server-side students in modal:", e);
      } finally {
        setFetchingStudents(false);
      }
    };

    fetchStudents();
  }, [isOpen, debouncedStudentSearch, preselectedStudent]);

  useEffect(() => {
    if (!isOpen) return;
    loadStationeries();
  }, [isOpen]);

  const loadInvoiceIntoForm = (inv: InvoiceItem) => {
    setCurrentEditingInvoice(inv);
    setSelectedStudentId(inv.student_id || preselectedStudent?.id || "");
    setAcademicSession(inv.academic_session || "2026/2027");
    setTerm(inv.term !== undefined ? String(inv.term) : "1");
    setDueDate(inv.due_date ? inv.due_date.split("T")[0] : "");
    setNotes(inv.notes || "Term fee invoice");

    const itemsToMap =
      inv.stationary_items && inv.stationary_items.length > 0
        ? inv.stationary_items
        : inv.line_items && inv.line_items.length > 0
        ? inv.line_items
        : [];

    if (itemsToMap.length > 0) {
      const rows = itemsToMap.map((item: any) => {
        const matched = allStationeries.find(
          (st) =>
            (item.stationary_id && st.id === item.stationary_id) ||
            (item.stationary?.id && st.id === item.stationary.id) ||
            (item.id && st.id === item.id) ||
            (item.description &&
              st.name.trim().toLowerCase() === item.description.trim().toLowerCase())
        );

        return {
          stationary_id: matched
            ? matched.id
            : item.stationary_id || item.stationary?.id || item.id || "",
          quantity: item.quantity !== undefined ? item.quantity : 1,
          discount_percentage:
            item.discount_percentage !== undefined ? item.discount_percentage : 0,
          customDescription: item.description || matched?.name || "",
          customUnitPrice:
            item.unit_price !== undefined ? item.unit_price : matched?.unit_price || 0,
        };
      });
      setStationaryRows(rows);
      const activeIndices = rows
        .map((r, i) => (Number(r.discount_percentage) > 0 ? i : -1))
        .filter((i) => i !== -1);
      setActiveDiscountRows(activeIndices);
    } else {
      setStationaryRows([{ stationary_id: "", quantity: 1, discount_percentage: 0 }]);
      setActiveDiscountRows([]);
    }
  };

  const handleSwitchToEdit = (inv: InvoiceItem) => {
    loadInvoiceIntoForm(inv);
    toast.success(`Switched to editing invoice ${inv.invoice_code || inv.id}`);
  };

  const handleResetToNew = () => {
    setCurrentEditingInvoice(null);
    const defaultDue = new Date();
    defaultDue.setDate(defaultDue.getDate() + 30);
    setDueDate(defaultDue.toISOString().split("T")[0]);
    setNotes("Term fee invoice");
    setStationaryRows([{ stationary_id: "", quantity: 1, discount_percentage: 0 }]);
    setActiveDiscountRows([]);
    toast.success("Ready to create a new invoice.");
  };

  // Fetch existing invoices when a student is selected
  useEffect(() => {
    if (!isOpen || !selectedStudentId) {
      setExistingStudentInvoices([]);
      return;
    }

    let isMounted = true;
    const fetchExisting = async () => {
      setFetchingExistingInvoices(true);
      try {
        const res = await getStudentInvoices({ student_id: selectedStudentId });
        if (isMounted) {
          if (res?.data && Array.isArray(res.data)) {
            setExistingStudentInvoices(res.data);
          } else if (Array.isArray(res)) {
            setExistingStudentInvoices(res);
          } else {
            setExistingStudentInvoices([]);
          }
        }
      } catch (e) {
        console.error("Error fetching existing student invoices:", e);
        if (isMounted) setExistingStudentInvoices([]);
      } finally {
        if (isMounted) setFetchingExistingInvoices(false);
      }
    };

    fetchExisting();
    return () => {
      isMounted = false;
    };
  }, [isOpen, selectedStudentId]);

  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      loadInvoiceIntoForm(initialData);
    } else {
      setCurrentEditingInvoice(null);
      setSelectedStudentId(preselectedStudent?.id || "");
      setAcademicSession("2026/2027");
      setTerm("1");
      setActiveDiscountRows([]);

      const defaultDue = new Date();
      defaultDue.setDate(defaultDue.getDate() + 30);
      setDueDate(defaultDue.toISOString().split("T")[0]);

      setNotes("Term fee invoice");
      if (preselectedStationeryId) {
        setStationaryRows([
          { stationary_id: preselectedStationeryId, quantity: 1, discount_percentage: 0 },
        ]);
      } else {
        setStationaryRows([{ stationary_id: "", quantity: 1, discount_percentage: 0 }]);
      }
    }
  }, [initialData, preselectedStudent, preselectedStationeryId, isOpen, allStationeries]);

  const toggleRowDiscount = (index: number) => {
    if (activeDiscountRows.includes(index)) {
      setActiveDiscountRows(activeDiscountRows.filter((i) => i !== index));
      handleRowChange(index, "discount_percentage", 0);
    } else {
      setActiveDiscountRows([...activeDiscountRows, index]);
    }
  };

  // Click outside listener for custom student dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!isOpen) return null;

  // Calculate row details, item discounts & overall total amount
  let grossSubtotal = 0;
  let itemDiscountsTotal = 0;

  const calculatedRows = stationaryRows.map((row) => {
    const matchedStationery = allStationeries.find((st) => st.id === row.stationary_id);
    const unitPrice = matchedStationery
      ? matchedStationery.unit_price
      : row.customUnitPrice !== undefined
      ? Number(row.customUnitPrice)
      : 0;
    const qty = Number(row.quantity) || 0;
    const discPercent = Math.min(100, Math.max(0, Number(row.discount_percentage) || 0));

    const grossAmount = qty * unitPrice;
    const discountAmount = (grossAmount * discPercent) / 100;
    const netAmount = grossAmount - discountAmount;

    grossSubtotal += grossAmount;
    itemDiscountsTotal += discountAmount;

    return {
      ...row,
      matchedStationery,
      unitPrice,
      grossAmount,
      discountPercent: discPercent,
      discountAmount,
      netAmount,
    };
  });

  const totalAmount = Math.max(0, grossSubtotal - itemDiscountsTotal);

  // Active student object to display
  const currentSelectedStudent =
    selectedStudentObj || allStudents.find((st) => st.id === selectedStudentId);

  const handleAddStationeryRow = () => {
    setStationaryRows([...stationaryRows, { stationary_id: "", quantity: 1, discount_percentage: 0 }]);
  };

  const handleRemoveStationeryRow = (index: number) => {
    if (stationaryRows.length <= 1) {
      toast.error("At least one stationery item is required.");
      return;
    }
    setStationaryRows(stationaryRows.filter((_, i) => i !== index));
  };

  const handleRowChange = (
    index: number,
    field: "stationary_id" | "quantity" | "discount_percentage",
    value: any
  ) => {
    const updated = [...stationaryRows];
    if (field === "quantity") {
      if (value === "") {
        updated[index][field] = "";
      } else {
        const numVal = Math.max(1, Number(value));
        updated[index][field] = isNaN(numVal) ? 1 : numVal;
      }
    } else if (field === "discount_percentage") {
      if (value === "") {
        updated[index][field] = "";
      } else {
        const numVal = Math.min(100, Math.max(0, Number(value)));
        updated[index][field] = isNaN(numVal) ? 0 : numVal;
      }
    } else {
      updated[index][field] = value;
    }
    setStationaryRows(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedStudentId) {
      toast.error("Please select a student");
      return;
    }
    if (!dueDate) {
      toast.error("Please select a due date");
      return;
    }
    if (stationaryRows.some((row) => !row.stationary_id)) {
      toast.error("Please select a stationery for all items");
      return;
    }

    setSubmitting(true);
    try {
      const lineItemsPayload = calculatedRows.map((row) => {
        const disc = Math.min(100, Math.max(0, Number(row.discount_percentage) || 0));
        const item: {
          description: string;
          quantity: number;
          unit_price: number;
          discount_percentage?: number;
        } = {
          description: row.matchedStationery?.name || row.customDescription || "School Item",
          quantity: Number(row.quantity) || 1,
          unit_price: Number(row.unitPrice) || 0,
        };

        if (disc > 0) {
          item.discount_percentage = disc;
        }

        return item;
      });

      const payload: InvoicePayload = {
        student_id: selectedStudentId,
        academic_session: academicSession,
        term: String(term || "1"),
        total_amount: totalAmount,
        due_date: dueDate,
        notes: notes.trim(),
        line_items: lineItemsPayload,
      };

      if (currentEditingInvoice?.id) {
        await updateInvoice({ id: currentEditingInvoice.id, payload });
        toast.success("Invoice updated successfully!");
      } else {
        await createInvoice(payload);
        toast.success("Invoice created successfully with discounts!");
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Failed to save invoice:", error);
      toast.error(
        error?.response?.data?.message || "Failed to save invoice. Please check parameters."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sessions = generateAcademicSessions(2025);

  // Existing invoice checks
  const matchingInvoice = existingStudentInvoices.find(
    (inv) =>
      inv.academic_session === academicSession &&
      String(inv.term) === String(term)
  );

  const isEditingCurrentMatch =
    currentEditingInvoice &&
    matchingInvoice &&
    currentEditingInvoice.id === matchingInvoice.id;

  const otherInvoices = existingStudentInvoices.filter(
    (inv) => !matchingInvoice || inv.id !== matchingInvoice.id
  );

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/65 backdrop-blur-sm flex items-center justify-center p-4 overflow-hidden animate-in fade-in duration-300">
      <div className="bg-white rounded-[2.5rem] w-full max-w-3xl max-h-[88vh] p-6 sm:p-8 shadow-2xl border border-slate-100 flex flex-col relative animate-in zoom-in-95 duration-200">
        
        {/* Header - Fixed top */}
        <div className="flex justify-between items-center pb-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border ${
              currentEditingInvoice?.id
                ? "bg-amber-50 text-amber-600 border-amber-200"
                : "bg-blue-50 text-blue-600 border-blue-100"
            }`}>
              <FileText size={24} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-2xl font-black text-slate-800">
                  {currentEditingInvoice?.id ? "Edit Student Invoice" : "Create Student Invoice"}
                </h3>
                {currentEditingInvoice?.id && (
                  <span className="text-[10px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 px-2.5 py-0.5 rounded-full border border-amber-200">
                    {currentEditingInvoice.invoice_code || "Editing"}
                  </span>
                )}
              </div>
              <p className="text-slate-400 font-bold text-xs">
                {currentEditingInvoice?.id
                  ? "Update invoice line items, amounts, and discount details."
                  : "Issue a new stationery invoice for the student."}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto py-4 px-2 space-y-6">
          
          {/* Searchable Student Selection Dropdown */}
          <div ref={dropdownRef} className="relative">
            <div className="flex items-center justify-between mb-2 ml-1">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                Select Student <span className="text-rose-500">*</span>
              </label>
              {fetchingExistingInvoices && (
                <span className="text-[10px] font-bold text-blue-600 flex items-center gap-1">
                  <Loader2 size={11} className="animate-spin" /> Checking invoices...
                </span>
              )}
            </div>

            {preselectedStudent ? (
              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center font-bold text-sm">
                    <User size={18} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 text-sm">
                      {preselectedStudent.name}
                    </h4>
                    <p className="text-xs text-slate-400 font-medium">
                      {preselectedStudent.classroom?.name ? `Class: ${preselectedStudent.classroom.name}` : preselectedStudent.email || preselectedStudent.id}
                    </p>
                  </div>
                </div>
                <span className="text-[10px] font-black uppercase tracking-wider bg-blue-100 text-blue-700 px-3 py-1.5 rounded-xl">
                  Selected
                </span>
              </div>
            ) : (
              <div className="relative">
                {/* Trigger Button / Selected display */}
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="w-full bg-[#F8FAFC] rounded-2xl border border-slate-200 px-4 py-3.5 flex items-center justify-between font-bold text-slate-800 text-sm focus:border-blue-400 focus:bg-white transition-all text-left"
                >
                  {currentSelectedStudent ? (
                    <div className="flex items-center gap-2 overflow-hidden">
                      <User size={16} className="text-blue-600 shrink-0" />
                      <span className="truncate">{currentSelectedStudent.name}</span>
                      {currentSelectedStudent.classroom?.name && (
                        <span className="bg-blue-50 text-blue-700 text-[10px] px-2 py-0.5 rounded-md font-bold shrink-0">
                          {currentSelectedStudent.classroom.name}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-slate-400 font-medium">
                      {fetchingStudents ? "Loading students..." : "-- Search or Select a Student --"}
                    </span>
                  )}
                  <ChevronDown size={18} className="text-slate-400 shrink-0" />
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl border border-slate-200 shadow-2xl z-30 p-3 space-y-2 max-h-64 flex flex-col animate-in fade-in zoom-in-95 duration-150">
                    {/* Embedded Search Input */}
                    <div className="relative flex items-center shrink-0">
                      <Search size={16} className="absolute left-3 text-slate-400" />
                      <input
                        type="text"
                        autoFocus
                        placeholder="Type student name to search (server-side)..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl pl-9 pr-8 py-2 text-xs font-bold text-slate-800 outline-none focus:border-blue-500 placeholder:text-slate-400"
                      />
                      {fetchingStudents && (
                        <Loader2 size={14} className="absolute right-3 text-blue-600 animate-spin" />
                      )}
                    </div>

                    {/* Options List */}
                    <div className="overflow-y-auto flex-1 divide-y divide-slate-50 pr-1">
                      {allStudents.length === 0 ? (
                        <div className="p-4 text-center text-xs text-slate-400 font-medium">
                          {fetchingStudents
                            ? "Searching students..."
                            : `No students found matching "${searchQuery}"`}
                        </div>
                      ) : (
                        allStudents.map((st) => {
                          const isSelected = selectedStudentId === st.id;
                          return (
                            <button
                              key={st.id}
                              type="button"
                              onClick={() => {
                                setSelectedStudentId(st.id);
                                setSelectedStudentObj(st);
                                setIsDropdownOpen(false);
                              }}
                              className={`w-full text-left p-3 rounded-xl flex items-center justify-between transition-colors ${
                                isSelected
                                  ? "bg-blue-50 text-blue-700 font-bold"
                                  : "hover:bg-slate-50 text-slate-700"
                              }`}
                            >
                              <div className="flex items-center gap-2 overflow-hidden">
                                <span className="font-bold text-xs truncate">{st.name}</span>
                                {st.classroom?.name && (
                                  <span className="bg-slate-100 text-slate-600 text-[10px] px-2 py-0.5 rounded-md font-bold shrink-0">
                                    {st.classroom.name}
                                  </span>
                                )}
                              </div>
                              {isSelected && <Check size={16} className="text-blue-600 shrink-0" />}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Existing Invoice UX Alerts & Warnings */}
          {matchingInvoice && (
            <div className="bg-amber-50/90 border-2 border-amber-300/80 rounded-2xl p-4 text-amber-900 shadow-sm animate-in fade-in zoom-in-95 duration-200">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 border border-amber-300 text-amber-700 flex items-center justify-center shrink-0 mt-0.5">
                  <AlertTriangle size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-extrabold text-sm text-amber-950">
                      Invoice Already Created for this Term
                    </span>
                    <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                      matchingInvoice.status === "paid"
                        ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                        : matchingInvoice.status === "partially_paid"
                        ? "bg-blue-100 text-blue-800 border-blue-300"
                        : "bg-amber-200 text-amber-900 border-amber-300"
                    }`}>
                      {matchingInvoice.status || "issued"}
                    </span>
                  </div>
                  <p className="text-xs text-amber-900 leading-relaxed font-medium mb-2.5">
                    <strong className="font-bold text-amber-950">{currentSelectedStudent?.name || "This student"}</strong> already has an invoice recorded for{" "}
                    <strong className="font-bold">{academicSession} (Term {term})</strong> with invoice code{" "}
                    <span className="font-mono font-bold bg-amber-200/80 px-1.5 py-0.5 rounded text-amber-950">
                      {matchingInvoice.invoice_code || matchingInvoice.id}
                    </span>.
                  </p>

                  <div className="grid grid-cols-3 gap-2 bg-white/80 border border-amber-200 rounded-xl p-2.5 mb-3 text-xs">
                    <div>
                      <p className="text-[10px] uppercase font-bold text-slate-400">Total</p>
                      <p className="font-black text-slate-800">₦{Number(matchingInvoice.total_amount || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase font-bold text-slate-400">Amount Paid</p>
                      <p className="font-black text-emerald-600">₦{Number(matchingInvoice.amount_paid || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase font-bold text-slate-400">Remaining</p>
                      <p className="font-black text-amber-800">
                        ₦{Number(
                          matchingInvoice.remaining_balance !== undefined
                            ? matchingInvoice.remaining_balance
                            : (matchingInvoice.total_amount || 0) - (matchingInvoice.amount_paid || 0)
                        ).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {!isEditingCurrentMatch ? (
                      <button
                        type="button"
                        onClick={() => handleSwitchToEdit(matchingInvoice)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs shadow-sm transition-all"
                      >
                        <Edit3 size={14} />
                        Edit Existing Invoice Instead
                      </button>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-emerald-800 bg-emerald-100 border border-emerald-200 px-3 py-1.5 rounded-xl flex items-center gap-1.5">
                          <Check size={14} className="text-emerald-600" /> Currently Editing This Existing Invoice
                        </span>
                        <button
                          type="button"
                          onClick={handleResetToNew}
                          className="text-xs font-bold text-slate-500 hover:text-slate-800 underline transition-colors"
                        >
                          Create new invoice instead
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notice for other terms/sessions */}
          {!matchingInvoice && otherInvoices.length > 0 && (
            <div className="bg-blue-50/90 border border-blue-200 rounded-2xl p-3.5 text-blue-900 animate-in fade-in duration-200">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center shrink-0 mt-0.5">
                  <FileText size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-blue-950">
                    Student has {otherInvoices.length} existing invoice{otherInvoices.length > 1 ? "s" : ""} in other sessions/terms:
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {otherInvoices.map((inv) => (
                      <button
                        key={inv.id}
                        type="button"
                        onClick={() => handleSwitchToEdit(inv)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-white border border-blue-200 hover:border-blue-400 text-slate-700 hover:text-blue-700 text-xs font-medium transition-all shadow-sm"
                      >
                        <span className="font-bold">{inv.academic_session} (Term {inv.term})</span>
                        <span className="text-slate-400 font-mono text-[11px]">
                          ₦{Number(inv.total_amount || 0).toLocaleString()}
                        </span>
                        <ArrowRight size={12} className="text-blue-500" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Session & Term */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                Academic Session
              </label>
              <select
                value={academicSession}
                onChange={(e) => setAcademicSession(e.target.value)}
                className="w-full bg-[#F8FAFC] rounded-2xl border border-slate-200 px-4 py-3.5 outline-none font-bold text-slate-700 text-sm focus:border-blue-400 focus:bg-white transition-all"
                required
              >
                {sessions.map(({ value: s }) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                Term
              </label>
              <select
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                className="w-full bg-[#F8FAFC] rounded-2xl border border-slate-200 px-4 py-3.5 outline-none font-bold text-slate-700 text-sm focus:border-blue-400 focus:bg-white transition-all"
                required
              >
                <option value="1">Term 1</option>
                <option value="2">Term 2</option>
                <option value="3">Term 3</option>
              </select>
            </div>
          </div>

          {/* Due Date & Notes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                Due Date <span className="text-rose-500">*</span>
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full bg-[#F8FAFC] rounded-2xl border border-slate-200 px-4 py-3.5 outline-none font-bold text-slate-700 text-sm focus:border-blue-400 focus:bg-white transition-all"
                required
              />
            </div>

            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">
                Invoice Notes
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. First term stationery invoice"
                className="w-full bg-[#F8FAFC] rounded-2xl border border-slate-200 px-4 py-3.5 outline-none font-bold text-slate-700 text-sm focus:border-blue-400 focus:bg-white transition-all"
              />
            </div>
          </div>

          {/* Stationery Items Selection Section */}
          <div className="pt-4 border-t border-slate-100">
            <div className="flex justify-between items-center mb-3">
              <div>
                <label className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                  <BookOpen size={16} className="text-blue-600" />
                  Stationery Items
                </label>
                <p className="text-[11px] text-slate-400 font-medium mt-0.5">
                  Select stationery items and optionally add percentage discounts.
                </p>
              </div>
              <button
                type="button"
                onClick={handleAddStationeryRow}
                className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-xl transition-all"
              >
                <Plus size={14} /> Add Stationery
              </button>
            </div>

            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {calculatedRows.map((row, idx) => {
                const isDiscountActive =
                  activeDiscountRows.includes(idx) || Number(row.discount_percentage) > 0;

                return (
                  <div
                    key={idx}
                    className="bg-[#F8FAFC] p-3.5 rounded-2xl border border-slate-200/80 hover:border-slate-300 transition-all flex flex-col gap-2.5"
                  >
                    <div className="grid grid-cols-12 gap-2 sm:gap-3 items-center">
                      {/* Select Stationery Item */}
                      <div
                        className={
                          isDiscountActive
                            ? "col-span-12 sm:col-span-5"
                            : "col-span-12 sm:col-span-5"
                        }
                      >
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">
                          Item #{idx + 1}
                        </label>
                        <select
                          value={row.stationary_id}
                          onChange={(e) =>
                            handleRowChange(idx, "stationary_id", e.target.value)
                          }
                          className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-800 outline-none focus:border-blue-400"
                          required
                        >
                          <option value="">-- Select Stationery Item --</option>
                          {row.stationary_id &&
                            !allStationeries.some((st) => st.id === row.stationary_id) && (
                              <option value={row.stationary_id}>
                                {row.customDescription || "Stationery Item"} (₦
                                {(row.customUnitPrice || 0).toLocaleString()})
                              </option>
                            )}
                          {allStationeries.map((st) => (
                            <option key={st.id} value={st.id}>
                              {st.name} (₦{(st.unit_price || 0).toLocaleString()})
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Quantity input */}
                      <div className="col-span-4 sm:col-span-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">
                          Qty
                        </label>
                        <input
                          type="number"
                          min="1"
                          placeholder="1"
                          value={row.quantity}
                          onWheel={(e) => (e.target as HTMLElement).blur()}
                          onChange={(e) =>
                            handleRowChange(idx, "quantity", e.target.value)
                          }
                          className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none focus:border-blue-400 text-center"
                          required
                        />
                      </div>

                      {/* If discount is active: show Disc (%) input. Otherwise, show initiate button */}
                      {isDiscountActive ? (
                        <div className="col-span-4 sm:col-span-2">
                          <label className="text-[10px] font-black text-emerald-600 uppercase tracking-wider flex items-center gap-1 mb-1">
                            <Percent size={11} /> Disc (%)
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              placeholder="0"
                              value={
                                row.discount_percentage === 0
                                  ? ""
                                  : row.discount_percentage
                              }
                              onWheel={(e) => (e.target as HTMLElement).blur()}
                              onChange={(e) =>
                                handleRowChange(
                                  idx,
                                  "discount_percentage",
                                  e.target.value
                                )
                              }
                              className="w-full bg-emerald-50/40 border border-emerald-200 text-emerald-800 rounded-xl pl-2 pr-6 py-2 text-xs font-bold outline-none focus:border-emerald-500 text-right"
                            />
                            <span className="absolute right-2 top-2 text-[10px] font-black text-emerald-600 pointer-events-none">
                              %
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="col-span-4 sm:col-span-2 flex items-end justify-center">
                          <button
                            type="button"
                            onClick={() => toggleRowDiscount(idx)}
                            className="w-full py-2 px-2 rounded-xl text-[11px] font-bold bg-slate-100 hover:bg-emerald-50 text-slate-600 hover:text-emerald-700 border border-slate-200/80 hover:border-emerald-200 flex items-center justify-center gap-1 transition-all"
                            title="Apply percentage discount to this item"
                          >
                            <Percent size={12} className="text-emerald-600" />
                            <span>Discount</span>
                          </button>
                        </div>
                      )}

                      {/* Line Total Calculation */}
                      <div className="col-span-3 sm:col-span-2 text-right">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">
                          Net Amount
                        </label>
                        <div className="font-black text-xs text-slate-800">
                          ₦{row.netAmount.toLocaleString()}
                        </div>
                        {row.discountAmount > 0 && (
                          <div className="flex items-center justify-end gap-1 text-[10px] font-bold text-emerald-600">
                            <span className="line-through text-slate-400 font-normal">
                              ₦{row.grossAmount.toLocaleString()}
                            </span>
                            <span>(-{row.discountPercent}%)</span>
                          </div>
                        )}
                      </div>

                      {/* Remove Button */}
                      <div className="col-span-1 flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveStationeryRow(idx)}
                          className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                          title="Remove item"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Quick percentage discount preset chips (only shown when discount is active) */}
                    {isDiscountActive && (
                      <div className="flex items-center justify-between gap-1.5 flex-wrap pt-1.5 border-t border-slate-200/50">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] font-bold text-slate-400 flex items-center gap-1 mr-1">
                            <Tag size={10} /> Presets:
                          </span>
                          {[0, 5, 10, 15, 20, 25, 50].map((preset) => {
                            const isActive = Number(row.discount_percentage) === preset;
                            return (
                              <button
                                key={preset}
                                type="button"
                                onClick={() =>
                                  handleRowChange(idx, "discount_percentage", preset)
                                }
                                className={`text-[10px] px-2.5 py-0.5 rounded-lg font-bold transition-all ${
                                  isActive
                                    ? "bg-emerald-600 text-white shadow-sm"
                                    : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                                }`}
                              >
                                {preset}%
                              </button>
                            );
                          })}
                        </div>

                        <button
                          type="button"
                          onClick={() => toggleRowDiscount(idx)}
                          className="text-[10px] font-bold text-rose-500 hover:text-rose-700 hover:underline px-1 py-0.5"
                        >
                          ✕ Remove Discount
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Invoice Summary & Savings Breakdown Card */}
          <div className="bg-slate-900 text-white rounded-2xl p-5 space-y-3 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <span className="text-xs font-bold text-slate-400">Gross Subtotal</span>
              <span className="text-sm font-black text-slate-200">
                ₦{grossSubtotal.toLocaleString()}
              </span>
            </div>

            {itemDiscountsTotal > 0 && (
              <div className="flex items-center justify-between text-xs text-emerald-400 font-bold">
                <span className="flex items-center gap-1">
                  <Tag size={13} /> Stationery Item Discounts
                </span>
                <span>-₦{itemDiscountsTotal.toLocaleString()}</span>
              </div>
            )}

            {itemDiscountsTotal > 0 && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-2.5 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-emerald-300 text-xs font-black">
                  <Sparkles size={14} className="text-amber-400" />
                  Total Parent Savings:
                </div>
                <div className="text-xs font-black text-emerald-300">
                  -₦{itemDiscountsTotal.toLocaleString()}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t border-slate-800">
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                  Final Payable Amount
                </span>
                <span className="text-2xl font-black text-emerald-400">
                  ₦{totalAmount.toLocaleString()}
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs text-slate-400 font-medium block">
                  {stationaryRows.length} item(s) listed
                </span>
                {itemDiscountsTotal > 0 && (
                  <span className="text-[10px] font-black text-emerald-400 uppercase tracking-wider">
                    Discount Applied
                  </span>
                )}
              </div>
            </div>
          </div>
        </form>

        {/* Sticky Action Footer */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 bg-white shrink-0 z-10">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting || totalAmount <= 0}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-xl flex items-center gap-2 disabled:opacity-50"
          >
            <Save size={16} />
            {submitting ? "Saving..." : initialData ? "Update Invoice" : "Issue Invoice"}
          </button>
        </div>

      </div>
    </div>
  );
};
